import { Injectable, inject } from '@angular/core';

import { filter } from 'rxjs/operators';

import { CookieName } from '../../browser/cookie/cookie.models';
import { CookieService } from '../../browser/cookie/cookie.service';
import { DateTimeService } from '../../browser/datetime.service';
import { Page } from '../../client-config/page.client-config';
import { RegistrationInformation } from '../../registration-info/registration-info.models';
import { RegistrationInfoService } from '../../registration-info/registration-info.service';
import { TrackerIdService } from '../../tracker-id/tracker-id.service';
import { UserEvent, UserUpdateEvent } from '../../user/user-events';
import { UserService } from '../../user/user.service';
import { DslCacheService } from '../dsl-cache.service';
import { DSL_NOT_READY, DslRecorderService } from '../dsl-recorder.service';
import { DslUserGroupAttributeResolverService } from '../dsl-user-group-attribute-resolver.service';
import { DslRecordable, DslValuesProvider } from '../dsl.models';
import { SegmentationGroupResolver } from '../segmentation-group-resolver';
import { UserGroupAttributesResolver } from '../user-group-attribute-resolver';

@Injectable()
export class UserDslValuesProvider implements DslValuesProvider {
    private readonly dslRecorderService = inject(DslRecorderService);
    private readonly user = inject(UserService);
    private readonly cookieService = inject(CookieService);
    private readonly trackerIdService = inject(TrackerIdService);
    private readonly dateTimeService = inject(DateTimeService);
    private readonly page = inject(Page);
    private readonly dslUserGroupAttributeResolverService = inject(DslUserGroupAttributeResolverService);
    private readonly segmentationGroupResolver = inject(SegmentationGroupResolver);
    private readonly registrationInfoService = inject(RegistrationInfoService);
    private readonly groupAttributeResolver = inject(UserGroupAttributesResolver);
    readonly dslCacheService = inject(DslCacheService);

    private registrationInformation: RegistrationInformation | null = null;

    constructor() {
        const user = this.user;
        const dslCacheService = this.dslCacheService;

        user.events.pipe(filter((e: UserEvent): e is UserUpdateEvent => e instanceof UserUpdateEvent)).subscribe((e) => {
            const refs: string[] = [];

            e.diff.forEach((_, prop: string) => refs.push(`user.${prop}`));

            dslCacheService.invalidate(refs);
        });

        this.registrationInfoService.registrationInformation.subscribe((regInfo: RegistrationInformation | null) => {
            this.registrationInformation = regInfo;

            dslCacheService.invalidate(['registrationInfo']);
        });
    }

    getProviders(): { [provider: string]: DslRecordable } {
        return {
            User: this.dslRecorderService
                .createRecordable('user')
                .createProperty({
                    name: 'RegistrationDate',
                    get: () => this.getRegistrationInformationValue<string>('date', ''),
                    deps: ['registrationInfo', 'user.isAuthenticated'],
                })
                .createSimpleProperty(this.user, 'country', 'Country')
                .createSimpleProperty(this.user, 'isFirstLogin', 'FirstLogin')
                .createSimpleProperty(this.user, 'visitCount', 'VisitCount')
                .createSimpleProperty(this.user, 'visitAfterDays', 'VisitAfterDays')
                .createProperty({ name: 'HasTracker', get: () => !!this.cookieService.get(CookieName.TrackerId), deps: 'cookie.Get.trackerId' })
                .createProperty({ name: 'TrackerId', get: () => this.trackerIdService.get(), deps: ['cookie.Get.trackerId', 'location'] })
                .createFunction({
                    name: 'IsInGroup',
                    get: (group: string) => this.segmentationGroupResolver.isInGroup(group),
                    deps: (group: string) => [
                        `user.IsInGroup.${group}.${this.user.isAuthenticated}`,
                        'user.isAuthenticated',
                        'user.segmentationGroups',
                    ],
                })
                .createFunction({
                    name: 'GetGroupAttribute',
                    get: (groupName: string, groupAttribute: string) =>
                        !this.user.isAuthenticated || !groupName || !groupAttribute ? '' : this.resolveUserGroupAttribute(groupName, groupAttribute),
                    deps: (groupName: string, attributeName: string) => [
                        `user.GetGroupAttribute.${groupName}.${attributeName}.${this.user.username}`,
                        'user.username',
                        'user.userGroupAttribute',
                    ],
                })
                .createProperty({ name: 'IsKnown', get: () => !!this.cookieService.get(CookieName.LastVisitor), deps: 'cookie.Get.lastVisitor' })
                .createSimpleProperty(this.user, 'realPlayer', 'IsRealPlayer')
                .createProperty({ name: 'Language', get: () => this.user.lang.toUpperCase(), deps: 'user.lang' })
                .createSimpleProperty(this.user, 'isAuthenticated', 'LoggedIn')
                .createSimpleProperty(this.user, 'username', 'LoginName')
                .createSimpleProperty(this.user, 'isAnonymous', 'IsAnonymous')
                .createSimpleProperty(this.user, 'loyalty', 'LoyaltyStatus')
                .createProperty({
                    name: 'LoyaltyPoints',
                    get: () => (this.user.isAuthenticated ? this.user.loyaltyPoints : -1),
                    deps: ['user.loyaltyPoints', 'user.isAuthenticated'],
                })
                .createProperty({
                    name: 'TierCode',
                    get: () => (this.user.isAuthenticated ? this.user.tierCode : -1),
                    deps: ['user.tierCode', 'user.isAuthenticated'],
                })
                .createProperty({
                    name: 'AffiliateInfo',
                    get: () => {
                        const cookieValue = this.cookieService.getObject(CookieName.MobileLoginPostLoginValues);

                        if (!cookieValue) return '';

                        return cookieValue['webmasterId'] || '';
                    },
                    deps: ['cookie.Get.mobileLogin.PostLoginValues', 'user.isAuthenticated'],
                })
                .createSimpleProperty(this.user, 'lastLoginTimeFormatted', 'LastLoginTimeFormatted')
                .createProperty({
                    name: 'DaysRegistered',
                    get: () => this.getRegistrationInformationValue<number>('daysRegistered', -1),
                    deps: ['registrationInfo', 'user.isAuthenticated'],
                })
                .createProperty({
                    name: 'Age',
                    get: () => {
                        if (!this.user.isAuthenticated || !this.user.dateOfBirth) {
                            return -1;
                        }

                        const today = this.dateTimeService.now();
                        const month = today.getUTCMonth() - this.user.dateOfBirth.getUTCMonth();
                        let years = today.getUTCFullYear() - this.user.dateOfBirth.getUTCFullYear();

                        if (month < 0 || (month === 0 && today.getUTCDate() < this.user.dateOfBirth.getUTCDate())) {
                            years--;
                        }

                        return years;
                    },
                    deps: ['user.dateOfBirth', 'user.isAuthenticated'],
                }),
        };
    }

    private resolveUserGroupAttribute(groupName: string, groupAttribute: string): any {
        if (this.page.featureFlags?.['userGroupAttributes']) {
            return this.groupAttributeResolver.userGroupAttribute(groupName, groupAttribute);
        }

        return this.dslUserGroupAttributeResolverService.resolve({ groupName: groupName, groupAttribute: groupAttribute })?.value;
    }

    private getRegistrationInformationValue<T>(property: string, defaultValue: T): T {
        if (!this.user.isAuthenticated) {
            return defaultValue;
        }

        return this.registrationInformation ? this.registrationInformation[property] : DSL_NOT_READY;
    }
}
